<template>
  <div class="container flex-grow-1 d-flex flex-column">
    <Loading v-if="loading" />

    <div v-else-if="users.length <= 0" class="d-flex flex-column align-items-center justify-content-center flex-grow-1">
      <div class="text-center">
        <font-awesome-icon :icon="['far', 'user-plus']" size="3x" class="mb-3 text-secondary" />
        <p class="mb-5 text-secondary">
          {{ $t("users.empty_message") }}
        </p>
        <button class="btn btn-primary text-white" v-if="
          user.claims.state === 'superadmin' || user.claims.state === 'admin'
        " @click="openModalCreate()">
          <font-awesome-icon class="me-2" :icon="['far', 'plus']" />
          {{ $t("users.new_user") }}
        </button>
      </div>
    </div>

    <div class="my-4" v-else>
      <div
        class="d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-center justify-content-between mb-4">
        <div class="input-group">
          <span class="input-group-text">
            <font-awesome-icon :icon="['far', 'magnifying-glass']" />
          </span>
          <input type="text" class="form-control" :placeholder="$t('users.search')" v-model="search" />
          <button v-if="search" class="btn btn-secondary" type="button" :aria-label="$t('home.clear_search')"
            @click="search = ''">
            <font-awesome-icon :icon="['far', 'xmark']" class="text-white" />
          </button>
        </div>

        <div class="d-flex align-items-center justify-content-end gap-3">
          <button class="btn btn-primary text-white text-nowrap" v-if="
            user.claims.state === 'superadmin' ||
            user.claims.state === 'admin'
          " @click="openModalCreate()">
            <font-awesome-icon class="me-2" :icon="['far', 'plus']" />
            {{ $t("users.new_user") }}
          </button>
        </div>
      </div>

      <div v-if="hasNoFilteredResults" class="alert alert-secondary text-center" role="status">
        {{ $t("users.no_filtered_results") }}
      </div>

      <ul class="list-group" v-else>
        <template v-for="item in filteredUsers" :key="item.uid">
          <li class="list-group-item d-flex justify-content-between align-items-center">

            <div class="d-flex flex-column gap-1">
              <div class="d-flex align-items-center gap-2">
                {{ item.displayName }}
                <StateLabel :state="item.customClaims?.state" />
                <span class="badge rounded-pill text-bg-danger" v-if="item.disabled">
                  {{ $t("users.disabled") }}
                </span>
                <span class="badge rounded-pill text-bg-info" v-if="isDeactivationScheduled(item.uid)">
                  {{ $t("users.deactivation_scheduled") }}
                </span>
                <span class="badge rounded-pill text-bg-warning" v-if="item.email.includes('hgdf.de')">
                  HGDF
                </span>
              </div>
              <small class="text-muted">{{ item.email }}</small>
            </div>


            <DropDownUsers v-if="item?.customClaims" v-show="!(
              user.claims.state === 'admin' &&
              item.customClaims.state === `superadmin`
            )
              " :id="item.uid" :is-disabled="item.disabled" @edit="
                openEditModal(item)
                " @deactivate="openDeactivationModal(item)" @reactivate="reactivateUserImmediately(item)"
              @delete="openDeleteModal(item)" @password="passwordUser(item)" />
          </li>
        </template>
      </ul>
    </div>

    <div class="modal fade" ref="modalSttings" data-bs-backdrop="static">
      <div class="modal-dialog modal-md  modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ selectedUserName }}
            </h5>
            <button type="button" class="btn-close" @click="modalSttings.hide()"></button>
          </div>
          <div class="modal-body">
            <h5>{{ $t("users.tools") }}</h5>
            <ul class="list-group" v-if="listOfTools.length > 0">
              <li
                class="list-group-item text-left d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2"
                v-for="(tool, index) in listOfTools" :key="index">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" :id="tool.id" :ref="tool.id"
                    :checked="tool.data.users.includes(selectedUser)" @change="setAsUser(tool.id, selectedUser)" />
                  <label class="form-check-label" :for="tool.id">
                    {{ tool.data.name }}
                  </label>
                </div>
                <button v-if="tool.data.users.includes(selectedUser) && hasClaimsToManage(tool.data.claims)"
                  type="button" class="btn btn-sm btn-outline-primary"
                  @click="openClaimsModal(tool.id, tool.data.name, tool.data.claims)">
                  <font-awesome-icon :icon="['far', 'sliders']" class="me-1" />
                  Claims
                </button>
              </li>
            </ul>

            <h5 class="mt-5">{{ $t("users.permission") }}</h5>
            <select class="form-select" :aria-label="$t('users.permission')" id="state" v-model="state"
              @change="setClaims(state, selectedUser)">
              <option value="false">{{ $t("users.user") }}</option>
              <option value="admin">{{ $t("users.admin") }}</option>
              <option value="superadmin" v-if="user.claims.state === 'superadmin'">
                {{ $t("users.superadmin") }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" ref="modalDeactivation" data-bs-backdrop="static">
      <div class="modal-dialog modal-md" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ $t("users.schedule_deactivation") }}: {{ selectedUserName }}
            </h5>
            <button type="button" class="btn-close" @click="modalDeactivation.hide()"></button>
          </div>
          <div class="modal-body">
            <div v-if="currentDeactivationSchedule?.status === 'scheduled'" class="alert alert-warning">
              <strong>{{ $t("users.deactivation_scheduled_for") }}:</strong>
              {{ formatScheduleDate(currentDeactivationSchedule.deactivateAt) }}
            </div>

            <div v-else-if="currentDeactivationSchedule?.status" class="alert alert-secondary">
              <strong>{{ $t("users.deactivation_status") }}:</strong>
              {{ currentDeactivationSchedule.status }}
            </div>

            <label for="deactivate-at" class="form-label">
              {{ $t("users.deactivate_at") }} (Europe/Berlin)
            </label>
            <input id="deactivate-at" type="datetime-local" class="form-control" v-model="deactivationAtLocal"
              :disabled="deactivationLoading" />
            <small class="text-muted">
              {{ $t("users.deactivation_timezone_hint") }}
            </small>
          </div>
          <div class="modal-footer d-flex justify-content-between">
            <button type="button" class="btn btn-outline-secondary" @click="modalDeactivation.hide()"
              :disabled="deactivationLoading">
              {{ $t("users.close") }}
            </button>

            <div class="d-flex gap-2">
              <button type="button" class="btn btn-outline-warning" @click="cancelDeactivationSchedule"
                :disabled="deactivationLoading || !isDeactivationScheduled(selectedUser)">
                {{ $t("users.cancel_deactivation") }}
              </button>
              <button type="button" class="btn btn-outline-success" @click="reactivateUserAccount"
                :disabled="deactivationLoading">
                {{ $t("users.reactivate_user") }}
              </button>
              <button type="button" class="btn btn-primary text-white" @click="scheduleDeactivation"
                :disabled="deactivationLoading || !deactivationAtLocal">
                <span v-if="deactivationLoading" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                {{ $t("users.save_deactivation") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SetupClaims ref="setupClaimsComponent" :uid="selectedUser" :toolId="selectedToolForClaims" :name="selectedUserName"
      :claims="selectedToolClaims" :hideButton="true" :toolName="selectedToolNameForClaims"
      :userEmail="selectedUserEmail" @hide-parent="hideUsersModal" @show-parent="showUsersModal" />

    <div class="modal fade" tabindex="-1" ref="modalDelete" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("users.delete") }}</h5>
          </div>

          <div class="modal-body" v-if="selectedUser">
            {{
              $t("users.user_delete_message", {
                user: users.find((el) => el.uid === selectedUser)?.displayName,
              })
            }}
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="modalDelete.hide()">
              {{ $t("users.close") }}
            </button>
            <button type="button" class="btn btn-danger" data-bs-dismiss="modal" style="color: white"
              @click="deleteUser()">
              {{ $t("users.delete") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" ref="modalCreate" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("users.new_user") }}</h5>
          </div>
          <div class="modal-body">
            <label for="name" class="form-label">
              {{ $t("users.name") }}
            </label>
            <div class="input-group mb-3">
              <input class="form-control" id="name" type="text" v-model="name" :disabled="formLoading" />
            </div>

            <label for="email" class="form-label">
              {{ $t("users.email") }}
            </label>
            <input class="form-control mb-3" id="email" type="email" v-model="email" :disabled="formLoading" />

            <label for="state" class="form-label">
              {{ $t("users.permission") }}
            </label>
            <select class="form-select mb-3" :aria-label="$t('users.permission')" id="state" v-model="state"
              :disabled="formLoading">
              <option value="false" selected="selected">
                {{ $t("users.user") }}
              </option>
              <option value="admin">{{ $t("users.admin") }}</option>
              <option value="superadmin" v-if="user.claims.state === 'superadmin'">
                {{ $t("users.superadmin") }}
              </option>
            </select>

            <label for="state" class="form-label">
              {{ $t("users.lang") }}
            </label>
            <select class="form-select" :aria-label="$t('users.lang')" id="state" v-model="lang"
              :disabled="formLoading">
              <option value="de" selected="selected">
                {{ $t("users.de") }}
              </option>
              <option value="en">{{ $t("users.en") }}</option>
              <option value="fr">{{ $t("users.fr") }}</option>
              <option value="es">{{ $t("users.es") }}</option>
            </select>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="modalCreate.hide()">
              {{ $t("users.close") }}
            </button>
            <button type="button" class="btn btn-primary text-white" @click="createUser()"
              :disabled="formLoading || !name || !email">
              <span v-if="formLoading" class="spinner-border spinner-border-sm me-2" role="status"
                aria-hidden="true"></span>
              {{ $t("users.create") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { firestore, functions } from "../plugins/firebase";
import { Modal } from "@comlinedev/bootstrap5";
import Loading from "./LoadingComponent.vue";
import DropDownUsers from "./smallComponents/DropDownUsers.vue";
import StateLabel from "./smallComponents/StateLabel.vue";
import SetupClaims from "./smallComponents/setupClaims.vue";
import {
  ActivityType,
  logActivity,
  logUserChange,
  logPermissionChange,
} from "../services/activityLogger";
import { setToolUserAccess } from "../services/toolAccessService";

export default {
  name: "UsersComponent",
  components: {
    Loading,
    DropDownUsers,
    StateLabel,
    SetupClaims,
  },
  computed: {
    ...mapGetters({
      user: "user",
    }),
    filteredUsers() {
      const term = this.search.trim().toLowerCase();
      if (!term) {
        return this.users;
      }
      return this.users.filter((user) => {
        const name = user.displayName?.toLowerCase() || "";
        const email = user.email?.toLowerCase() || "";

        return name.includes(term) || email.includes(term);
      });
    },
    hasNoFilteredResults() {
      return (
        !this.loading &&
        this.users.length > 0 &&
        this.filteredUsers.length === 0
      );
    },
    currentDeactivationSchedule() {
      if (!this.selectedUser) {
        return null;
      }
      return this.getScheduleForUser(this.selectedUser);
    },
  },
  data() {
    return {
      listOfTools: [],
      loading: true,
      selectedUser: null,
      selectedUserName: "",
      selectedUserEmail: "",
      selectedToolForClaims: null,
      selectedToolNameForClaims: "",
      selectedToolClaims: [],
      error: null,
      users: [],
      name: "",
      email: "",
      state: "false",
      lang: "de",
      search: "",
      formLoading: false,
      deactivationLoading: false,
      deactivationAtLocal: "",
      deactivationSchedules: {},
    };
  },
  methods: {
    getScheduleForUser(uid) {
      return this.deactivationSchedules[uid] || null;
    },
    isDeactivationScheduled(uid) {
      return this.getScheduleForUser(uid)?.status === "scheduled";
    },
    formatScheduleDate(dateValue) {
      if (!dateValue) {
        return "-";
      }

      return new Date(dateValue).toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    toBerlinDateTimeLocal(dateValue) {
      if (!dateValue) {
        return "";
      }

      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return "";
      }

      const formatter = new Intl.DateTimeFormat("sv-SE", {
        timeZone: "Europe/Berlin",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return formatter.format(date).replace(" ", "T");
    },
    parseBerlinDateTimeToIso(localDateTime) {
      if (!localDateTime) {
        return null;
      }

      const [datePart, timePart] = localDateTime.split("T");
      if (!datePart || !timePart) {
        return null;
      }

      const [year, month, day] = datePart.split("-").map((val) => Number(val));
      const [hour, minute] = timePart.split(":").map((val) => Number(val));

      if (
        [year, month, day, hour, minute].some((val) => Number.isNaN(val))
      ) {
        return null;
      }

      const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute));
      const berlinParts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Berlin",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(utcGuess);

      const mapped = Object.fromEntries(
        berlinParts
          .filter((part) => part.type !== "literal")
          .map((part) => [part.type, Number(part.value)])
      );

      const targetUtcMs = Date.UTC(year, month - 1, day, hour, minute);
      const berlinAsUtcMs = Date.UTC(
        mapped.year,
        mapped.month - 1,
        mapped.day,
        mapped.hour,
        mapped.minute
      );
      const correctedDate = new Date(utcGuess.getTime() + (targetUtcMs - berlinAsUtcMs));

      return correctedDate.toISOString();
    },
    async loadDeactivationSchedules() {
      try {
        const runDueSchedules = httpsCallable(
          functions,
          "runScheduledUserDeactivationsNow"
        );
        await runDueSchedules();

        const getSchedules = httpsCallable(functions, "getUserDeactivationSchedules");
        const result = await getSchedules();
        const scheduleList = Array.isArray(result?.data?.schedules)
          ? result.data.schedules
          : [];

        this.deactivationSchedules = scheduleList.reduce((acc, item) => {
          acc[item.uid] = item;
          return acc;
        }, {});
      } catch (err) {
        console.error(err);
        this.$store.commit("addError", err);
      }
    },
    async refreshDeactivationSchedule(uid) {
      try {
        const getSchedule = httpsCallable(functions, "getUserDeactivationSchedule");
        const result = await getSchedule({ uid });
        if (!result?.data?.exists) {
          delete this.deactivationSchedules[uid];
          return;
        }

        this.deactivationSchedules = {
          ...this.deactivationSchedules,
          [uid]: result.data,
        };
      } catch (err) {
        console.error(err);
        this.$store.commit("addError", err);
      }
    },
    openEditModal(item) {
      this.selectedUser = item.uid;
      this.state = item?.customClaims?.state || "false";
      this.selectedUserName = item.displayName || "";
      this.modalSttings.show();
    },
    openDeleteModal(item) {
      this.selectedUser = item.uid;
      this.selectedUserName = item.displayName || "";
      this.modalDelete.show();
    },
    async openDeactivationModal(item) {
      this.selectedUser = item.uid;
      this.selectedUserName = item.displayName || "";
      await this.refreshDeactivationSchedule(item.uid);

      const schedule = this.getScheduleForUser(item.uid);
      this.deactivationAtLocal = this.toBerlinDateTimeLocal(schedule?.deactivateAt);
      this.modalDeactivation.show();
    },
    async scheduleDeactivation() {
      const deactivateAt = this.parseBerlinDateTimeToIso(this.deactivationAtLocal);
      if (!deactivateAt) {
        this.$store.commit("addError", this.$t("users.invalid_deactivation_date"));
        return;
      }

      const previousSchedule = this.getScheduleForUser(this.selectedUser);
      this.deactivationLoading = true;
      try {
        const scheduleUserDeactivation = httpsCallable(
          functions,
          "scheduleUserDeactivation"
        );
        await scheduleUserDeactivation({
          uid: this.selectedUser,
          deactivateAt,
        });

        await this.refreshDeactivationSchedule(this.selectedUser);
        this.$store.commit("addSuccess", this.$t("users.success_schedule_deactivation"));

        const targetUser = this.users.find((el) => el.uid === this.selectedUser);
        await logActivity(ActivityType.USER_DEACTIVATION_SCHEDULED, {
          targetUser: targetUser?.email || this.selectedUser,
          targetUserName: targetUser?.displayName || this.selectedUserName,
          before: { deactivationStatus: previousSchedule?.status || null },
          after: {
            deactivationStatus: "scheduled",
            deactivateAt,
            timezone: "Europe/Berlin",
          },
        });
      } catch (err) {
        console.error(err);
        this.$store.commit("addError", err);
      } finally {
        this.deactivationLoading = false;
      }
    },
    async cancelDeactivationSchedule() {
      this.deactivationLoading = true;
      try {
        const cancelUserDeactivation = httpsCallable(
          functions,
          "cancelUserDeactivation"
        );
        await cancelUserDeactivation({ uid: this.selectedUser });
        await this.refreshDeactivationSchedule(this.selectedUser);
        this.$store.commit("addSuccess", this.$t("users.success_cancel_deactivation"));

        const targetUser = this.users.find((el) => el.uid === this.selectedUser);
        await logActivity(ActivityType.USER_DEACTIVATION_CANCELLED, {
          targetUser: targetUser?.email || this.selectedUser,
          targetUserName: targetUser?.displayName || this.selectedUserName,
          after: { deactivationStatus: "cancelled" },
        });
      } catch (err) {
        console.error(err);
        this.$store.commit("addError", err);
      } finally {
        this.deactivationLoading = false;
      }
    },
    async reactivateUserById(uid, displayName, email) {
      this.deactivationLoading = true;
      try {
        const reactivateUser = httpsCallable(functions, "reactivateUser");
        await reactivateUser({ uid });
        await Promise.all([this.refreshDeactivationSchedule(uid), this.loadData()]);
        this.$store.commit("addSuccess", this.$t("users.success_reactivate_user"));

        await logActivity(ActivityType.USER_REACTIVATED, {
          targetUser: email || uid,
          targetUserName: displayName || uid,
          before: { disabled: true },
          after: { disabled: false },
        });
      } catch (err) {
        console.error(err);
        this.$store.commit("addError", err);
      } finally {
        this.deactivationLoading = false;
      }
    },
    async reactivateUserImmediately(item) {
      this.selectedUser = item.uid;
      this.selectedUserName = item.displayName || "";
      await this.reactivateUserById(item.uid, item.displayName, item.email);
    },
    async reactivateUserAccount() {
      const targetUser = this.users.find((el) => el.uid === this.selectedUser);
      await this.reactivateUserById(
        this.selectedUser,
        targetUser?.displayName || this.selectedUserName,
        targetUser?.email || this.selectedUser
      );
    },
    hasClaimsToManage(claims) {
      if (!claims || !Array.isArray(claims)) return false;
      return claims.some(claim => !claim.required);
    },
    openClaimsModal(toolId, toolName, claims) {
      this.selectedToolForClaims = toolId;
      this.selectedToolNameForClaims = toolName;
      this.selectedToolClaims = claims || [];

      // Get user email
      const user = this.users.find(u => u.uid === this.selectedUser);
      this.selectedUserEmail = user?.email || "";

      this.$nextTick(() => {
        if (this.$refs.setupClaimsComponent) {
          this.$refs.setupClaimsComponent.openModal();
        }
      });
    },
    hideUsersModal() {
      if (this.modalSttings) {
        this.modalSttings.hide();
      }
    },
    showUsersModal() {
      if (this.modalSttings) {
        this.modalSttings.show();
      }
    },
    async reload() {
      this.loading = true;
      await Promise.all([this.loadData(), this.loadDeactivationSchedules()]);
      this.loading = false;
    },
    async loadData() {
      const getUsersFromAuth = httpsCallable(functions, "getUsers");
      await getUsersFromAuth()
        .then((result) => {
          this.users = result.data.users
            .filter((user) => user.uid !== this.user.data.uid)
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
        })
        .catch((err) => {
          console.error(err);
          this.$store.commit("addError", err);
        });
      return "ok";
    },
    openModalCreate() {
      this.name = "";
      this.email = "";
      this.state = "false";
      this.lang = "de";
      this.modalCreate.show();
    },
    async createUser() {
      this.formLoading = true;
      const createUserAccount = httpsCallable(functions, "createUser");

      await createUserAccount({
        email: this.email,
        name: this.name,
        state: this.state,
        lang: this.lang,
      })
        .then(async () => {
          this.$store.commit(
            "addSuccess",
            this.$t("users.success_create", { name: this.name })
          );

          // Log activity
          await logUserChange(
            ActivityType.USER_CREATED,
            this.email,
            this.name,
            null,
            { permission: this.state, lang: this.lang }
          );

          this.modalCreate.hide();
          this.name = null;
          this.email = null;
          this.state = "false";
          this.lang = "de";
          this.reload();
        })
        .catch((err) => {
          this.$store.commit("addError", err);
        })
        .finally(() => {
          this.formLoading = false;
        });
    },
    async deleteUser() {
      let deleteUserAccount = await httpsCallable(functions, "deleteUser");
      const targetUser = this.users.find((el) => el.uid === this.selectedUser);
      const userName = targetUser?.displayName || "";
      const userEmail = targetUser?.email || "";

      await deleteUserAccount({ uid: this.selectedUser })
        .then(async () => {
          this.$store.commit(
            "addSuccess",
            this.$t("users.success_delete", { name: userName })
          );

          // Log activity
          await logUserChange(
            ActivityType.USER_DELETED,
            userEmail,
            userName,
            { permission: targetUser?.customClaims?.state },
            null
          );

          this.reload();
        })
        .catch((err) => {
          console.error(err);
          this.reload();
        });
    },
    async passwordUser(item) {
      this.selectedUser = item.uid;
      let resetPassword = await httpsCallable(functions, "sendPasswordReset");
      const targetUser = this.users.find((el) => el.uid === this.selectedUser);
      const userName = targetUser?.displayName || "";
      const userEmail = targetUser?.email || "";

      await resetPassword({ uid: this.selectedUser })
        .then(async () => {
          this.$store.commit(
            "addSuccess",
            this.$t("users.success_password", { name: userName })
          );

          // Log activity
          await logUserChange(
            ActivityType.USER_PASSWORD_RESET,
            userEmail,
            userName
          );
        })
        .catch((err) => {
          console.error(err);
        });
    },
    async getListOfTools() {
      const qOnlyMy = query(
        collection(firestore, "tools"),
        where("users", "array-contains", this.user.data.uid),
        orderBy("name")
      );
      const qAll = query(collection(firestore, "tools"), orderBy("name"));

      const q = this.user.claims.state === "superadmin" ? qAll : qOnlyMy;
      const querySnapshot = await getDocs(q);
      this.listOfTools = [];
      querySnapshot.forEach((doc) => {
        this.listOfTools.push({ data: doc.data(), id: doc.id });
      });
    },
    async setAsUser(tool, user) {
      const state = this.$refs[tool]?.[0]?.checked;
      const toolData = this.listOfTools.find((el) => el.id === tool)?.data;
      const claimsRequired = (toolData?.claims || []).find((el) => el?.required);
      const targetUser = this.users.find((el) => el.uid === user);
      const userName = targetUser?.displayName || "";
      const userEmail = targetUser?.email || "";

      if (typeof state !== "boolean" || !toolData) {
        return;
      }

      try {
        await setToolUserAccess({
          toolId: tool,
          toolName: toolData.name,
          uid: user,
          accessGranted: state,
          requiredClaim: claimsRequired,
          userEmail,
          userName,
        });

        await this.getListOfTools();
      } catch (err) {
        console.error(err);
        this.$store.commit("addError", err);
      }
    },
    async setClaims(state, uid) {
      let setUserCustomClaims = await httpsCallable(
        functions,
        "setUserCustomClaims"
      );

      const targetUser = this.users.find((el) => el.uid === uid);
      const userName = targetUser?.displayName || "";
      const userEmail = targetUser?.email || "";
      const oldPermission = targetUser?.customClaims?.state || "false";

      await setUserCustomClaims({ uid: uid, state: state })
        .then(async () => {
          // Log activity
          await logPermissionChange(
            userEmail,
            userName,
            oldPermission,
            state
          );

          this.reload();
        })
        .catch((err) => this.$store.commit("addError", err));
    },
  },
  mounted() {
    this.getListOfTools();
    Promise.all([this.loadData(), this.loadDeactivationSchedules()]).then(() => {
      this.loading = false;
    });
    this.modalSttings = new Modal(this.$refs.modalSttings);
    this.modalDeactivation = new Modal(this.$refs.modalDeactivation);
    this.modalDelete = new Modal(this.$refs.modalDelete);
    this.modalCreate = new Modal(this.$refs.modalCreate);
  },
};
</script>
